import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getOrganization,
  updateOrganization,
  deleteOrganization,
  inviteMember,
  updateMemberRole,
  removeMember,
} from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import { ArtistRoster } from "@/components/ArtistRoster";
import { Toggle } from "@/components/ds";
import {
  Building2,
  Crown,
  Mail,
  Music,
  Shield,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users,
} from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS: Record<string, string> = {
  owner: "Owner",
  manager: "Manager",
  member: "Member",
};

const ROLE_COLORS: Record<string, string> = {
  owner: "text-warn bg-warn/15 border border-warn/30",
  manager: "text-info bg-info/15 border border-info/30",
  member: "text-ink-3 bg-panel-3 border border-line-2",
};

export function OrganizationPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const loadOrganizations = useAuthStore((s) => s.loadOrganizations);

  const { data: org, isLoading } = useQuery({
    queryKey: ["organization", id],
    queryFn: () => getOrganization(id!),
    enabled: !!id,
  });

  const [activeTab, setActiveTab] = useState<"members" | "artists">("members");
  const [editName, setEditName] = useState(false);
  const [name, setName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [showInvite, setShowInvite] = useState(false);

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string }) => updateOrganization(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", id] });
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
      setEditName(false);
    },
  });

  const safeEditMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      updateOrganization(id!, { safe_edit_enabled: enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", id] });
      queryClient.invalidateQueries({ queryKey: ["organizations"] });
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to update safe edit");
    },
  });

  const inviteMutation = useMutation({
    mutationFn: () => inviteMember(id!, inviteEmail, inviteRole),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", id] });
      setInviteEmail("");
      setInviteRole("member");
      setShowInvite(false);
      toast.success("Invite sent");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to send invite");
    },
  });

  const roleMutation = useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: string }) =>
      updateMemberRole(id!, memberId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", id] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (memberId: string) => removeMember(id!, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", id] });
      toast.success("Member removed");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to remove member");
    },
  });

  const deleteOrgMutation = useMutation({
    mutationFn: () => deleteOrganization(id!),
    onSuccess: async () => {
      await loadOrganizations();
      toast.success("Organization deleted");
      navigate("/dashboard");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to delete organization");
    },
  });

  // Current user's role in this org
  const myMembership = org?.members.find((m) => m.user_id === user?.id);
  const canManage =
    myMembership?.role === "owner" || myMembership?.role === "manager";
  const isOwner = myMembership?.role === "owner";
  const canConfigureSafeEdit = isOwner || Boolean(user?.is_admin);

  if (isLoading) {
    return <p className="text-sm text-ink-3">Loading organization...</p>;
  }

  if (!org) {
    return <p className="text-sm text-bad">Organization not found</p>;
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-panel-3 text-ink-2">
            <Building2 className="h-6 w-6" />
          </div>
          <div>
            {editName ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  updateMutation.mutate({ name });
                }}
                className="flex items-center gap-2"
              >
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input w-auto px-2 py-1 text-lg font-semibold"
                  autoFocus
                />
                <button
                  type="submit"
                  className="btn btn--accent px-2 py-1 text-xs"
                >
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => setEditName(false)}
                  className="text-xs text-ink-3 hover:text-ink-2"
                >
                  Cancel
                </button>
              </form>
            ) : (
              <h2
                className={`text-2xl font-semibold tracking-tight ${canManage ? "cursor-pointer hover:text-ink-2" : ""}`}
                onClick={() => {
                  if (canManage) {
                    setName(org.name);
                    setEditName(true);
                  }
                }}
              >
                {org.name}
              </h2>
            )}
            <p className="text-sm text-ink-3">
              {org.is_personal ? "Personal workspace" : org.slug}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {canConfigureSafeEdit && (
            <div className="flex h-9 items-center gap-2 rounded-ds-sm border border-line-2 bg-panel px-2.5">
              <ShieldCheck
                className={`h-3.5 w-3.5 ${org.safe_edit_enabled ? "text-accent" : "text-ink-4"}`}
              />
              <span className="text-xs font-medium text-ink-3">Safe edit</span>
              <Toggle
                checked={org.safe_edit_enabled}
                onChange={(next) => safeEditMutation.mutate(next)}
                label="Safe edit"
                size="mini"
                disabled={safeEditMutation.isPending}
              />
            </div>
          )}
          {isOwner && !org.is_personal && (
            <button
              onClick={() => {
                if (
                  confirm(
                    `Delete "${org.name}"? Artist profiles will be kept, but they will no longer be associated with this organization.`,
                  )
                ) {
                  deleteOrgMutation.mutate();
                }
              }}
              disabled={deleteOrgMutation.isPending}
              className="flex items-center gap-1.5 rounded-md border border-line-3 px-3 py-1.5 text-sm text-ink-3 transition-colors hover:border-bad/50 hover:bg-bad/10 hover:text-bad disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              {deleteOrgMutation.isPending ? "Deleting..." : "Delete"}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setActiveTab("members")}
          className={`flex items-center gap-1.5 rounded-full text-sm px-3 py-1.5 transition-colors ${
            activeTab === "members"
              ? "bg-panel-3 text-ink"
              : "text-ink-3 hover:text-ink"
          }`}
        >
          <Users className="h-4 w-4" />
          Members
        </button>
        <button
          onClick={() => setActiveTab("artists")}
          className={`flex items-center gap-1.5 rounded-full text-sm px-3 py-1.5 transition-colors ${
            activeTab === "artists"
              ? "bg-panel-3 text-ink"
              : "text-ink-3 hover:text-ink"
          }`}
        >
          <Music className="h-4 w-4" />
          Artists
        </button>
      </div>

      {/* Members tab */}
      {activeTab === "members" && (
        <div>
          <div className="flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-lg font-medium">
              <Users className="h-5 w-5 text-ink-3" />
              Members
              <span className="text-sm text-ink-3">({org.members.length})</span>
            </h3>
            {canManage && !org.is_personal && (
              <button
                onClick={() => setShowInvite(true)}
                className="btn btn--ghost gap-1.5 px-3 py-1.5"
              >
                <UserPlus className="h-4 w-4" />
                Invite
              </button>
            )}
          </div>

          {/* Invite form */}
          {showInvite && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                inviteMutation.mutate();
              }}
              className="card mt-4 flex items-end gap-3 p-4"
            >
              <div className="flex-1 space-y-1.5">
                <label className="text-xs font-medium text-ink-3">Email</label>
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@example.com"
                  className="input h-9"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-ink-3">Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="input h-9 w-auto"
                >
                  <option value="member">Member</option>
                  <option value="manager">Manager</option>
                </select>
              </div>
              <button
                type="submit"
                disabled={inviteMutation.isPending}
                className="btn btn--ghost h-9 px-4"
              >
                {inviteMutation.isPending ? "Sending..." : "Send Invite"}
              </button>
              <button
                type="button"
                onClick={() => setShowInvite(false)}
                className="btn btn--ghost h-9 px-3"
              >
                Cancel
              </button>
            </form>
          )}

          {inviteMutation.error && (
            <p className="mt-2 text-sm text-bad">
              {inviteMutation.error instanceof Error
                ? inviteMutation.error.message
                : "Failed to invite"}
            </p>
          )}

          {/* Member list */}
          <div className="mt-4 space-y-1">
            {org.members.map((member) => (
              <div
                key={member.id}
                className="flex flex-wrap items-center gap-3 rounded-lg px-3 py-3 transition-colors hover:bg-panel-2 sm:px-4"
              >
                {/* Avatar + name block — always together, takes the row's
                    available width and pushes badges/controls to wrap below
                    on narrow viewports. */}
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-panel-3 text-xs font-medium text-ink-2">
                    {(member.user_name || member.user_email)
                      .charAt(0)
                      .toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink truncate">
                      {member.user_name || member.user_email}
                      {member.user_id === user?.id && (
                        <span className="ml-2 text-xs text-ink-3">(you)</span>
                      )}
                    </p>
                    <p className="text-xs text-ink-3 truncate">
                      {member.user_email}
                    </p>
                  </div>
                </div>

                {/* Badges + controls — wraps to its own line on mobile when
                    the name block is wide. */}
                <div className="ml-11 flex flex-wrap items-center gap-2 sm:ml-0">
                  {/* Role badge */}
                  <span
                    className={`flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${ROLE_COLORS[member.role] || ROLE_COLORS.member}`}
                  >
                    {member.role === "owner" && <Crown className="h-3 w-3" />}
                    {member.role === "manager" && (
                      <Shield className="h-3 w-3" />
                    )}
                    {ROLE_LABELS[member.role] || member.role}
                  </span>

                  {/* Status badge for pending invites */}
                  {member.invite_status === "pending" && (
                    <span className="flex items-center gap-1 rounded-md border border-warn/30 bg-warn/15 px-2 py-0.5 text-xs font-medium text-warn">
                      <Mail className="h-3 w-3" />
                      Pending
                    </span>
                  )}

                  {/* Actions */}
                  {canManage &&
                    member.user_id !== user?.id &&
                    member.role !== "owner" && (
                      <>
                        {isOwner && (
                          <select
                            value={member.role}
                            onChange={(e) =>
                              roleMutation.mutate({
                                memberId: member.id,
                                role: e.target.value,
                              })
                            }
                            className="input h-8 w-auto px-2.5"
                          >
                            <option value="member">Member</option>
                            <option value="manager">Manager</option>
                          </select>
                        )}
                        <button
                          onClick={() => {
                            if (
                              confirm(
                                `Remove ${member.user_name || member.user_email} from the organization?`,
                              )
                            ) {
                              removeMutation.mutate(member.id);
                            }
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-md border border-line-3 text-ink-3 transition-colors hover:border-bad/50 hover:bg-bad/10 hover:text-bad"
                          title="Remove member"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Artists tab */}
      {activeTab === "artists" && (
        <ArtistRoster
          orgId={id!}
          canManage={canManage}
          isPersonal={org.is_personal}
        />
      )}
    </div>
  );
}
